import { useId } from 'react';
import { Appearance } from './Appearance.js';
import { deviceId } from './clock.js';
import { ChangePassword } from './ChangePassword.js';
import { CurrencyChoice } from './CurrencyChoice.js';
import { Reminders } from './Reminders.js';
import { TaxonomyManager } from './TaxonomyManager.js';
import type { AnyEvent, Manifest } from '@intentionalist/shared';
import type { HealthResponse } from '@intentionalist/shared';
import type { StorageDurability } from './storage.js';
import type { SyncState } from './engine.js';
import type { View } from './navigation.js';

/**
 * Settings: account, reminders, organizing, and diagnostics.
 *
 * Diagnostics are here rather than on the front page, which is where they used
 * to be. The deployed commit has to be *visible* (NFR-CON-10), not prominent --
 * it is there so a bug report can name a build, and a user who has never needed
 * it should not have read it every time they opened the app.
 *
 * Organizing lives here too. Renaming a category is arguably a catalog-shaped
 * thought, but it is a rare one, and putting it among the four destinations
 * would have given equal weight to something done twice a year.
 */
export function Settings({
  view,
  manifest,
  version,
  onWrite,
  userId,
  onSignOut,
  sync,
  health,
  healthError,
  durability,
  commit,
  onSync,
}: {
  view: View;
  manifest: Manifest;
  version: number;
  onWrite: (events: AnyEvent[]) => void;
  userId: string;
  onSignOut: () => void;
  sync: SyncState;
  health: HealthResponse | null;
  healthError: string | null;
  durability: StorageDurability | null;
  commit: string;
  onSync: () => void;
}) {
  if (view === 'appearance') return <Appearance />;

  if (view === 'reminders') return <Reminders />;

  if (view === 'organize') {
    return <TaxonomyManager manifest={manifest} version={version} onWrite={onWrite} />;
  }

  if (view === 'diagnostics') {
    return (
      <Diagnostics
        sync={sync}
        health={health}
        healthError={healthError}
        durability={durability}
        commit={commit}
        onSync={onSync}
      />
    );
  }

  return <Account userId={userId} onSignOut={onSignOut} />;
}

/**
 * Account, and the one-off preferences that have nowhere better to live.
 *
 * The currency is here rather than behind a tab of its own. A settings menu that
 * grows an entry per preference stops being navigation, and a thing set once
 * does not earn equal billing with Organize or Diagnostics.
 *
 * It sits below the identity and above Sign out, so the destructive control
 * stays last on the page -- the order the item details screen also follows.
 */
function Account({ userId, onSignOut }: { userId: string; onSignOut: () => void }) {
  return (
    <div>
      <h2>Account</h2>
      <dl className="sync-state">
        <dt>Signed in as</dt>
        <dd>
          <code>{userId}</code>
        </dd>
      </dl>

      <CurrencyChoice />

      <ChangePassword />

      <div className="actions">
        <button type="button" onClick={onSignOut}>
          Sign out
        </button>
      </div>
    </div>
  );
}

/**
 * What a bug report needs.
 *
 * The storage warning is the one thing here a user should act on: without
 * persistent storage the browser may evict unsynced changes, which is the single
 * place this system can lose work (risk R4). It is phrased as a consequence
 * rather than as a status for that reason.
 */
function Diagnostics({
  sync,
  health,
  healthError,
  durability,
  commit,
  onSync,
}: {
  sync: SyncState;
  health: HealthResponse | null;
  healthError: string | null;
  durability: StorageDurability | null;
  commit: string;
  onSync: () => void;
}) {
  const ids = { storage: useId() };

  return (
    <div>
      <h2>Diagnostics</h2>

      <dl className="sync-state">
        <dt>Client build</dt>
        <dd>
          <code>{commit}</code>
        </dd>

        <dt>Service</dt>
        <dd>
          {health ? (
            <>
              <code>{health.commit}</code> · {health.env}
            </>
          ) : (
            <span className="muted">{healthError ?? 'checking…'}</span>
          )}
        </dd>

        <dt>Device</dt>
        <dd>
          <code>{deviceId().slice(0, 8)}</code>
        </dd>

        <dt>Changes waiting</dt>
        {/*
          Live, because this is the row someone opens this screen to watch: they
          pressed "Sync now" and are waiting for the count to fall.
        */}
        <dd aria-live="polite">
          {sync.pending === 0 ? (
            'none'
          ) : (
            // Until these are pushed they exist on this device and nowhere else
            // (risk R4), which is why the count is stated rather than implied.
            <strong>
              {sync.pending} change{sync.pending === 1 ? '' : 's'} waiting
            </strong>
          )}
        </dd>

        <dt>Last sync</dt>
        <dd>
          {sync.error !== null ? (
            // Plain language, no status code (NFR-UX-8). It sits on the row it
            // describes rather than above the list, so what failed is obvious.
            <span className="warn">{sync.error}</span>
          ) : sync.lastSyncAt === null ? (
            <span className="muted">not yet</span>
          ) : (
            new Date(sync.lastSyncAt).toLocaleTimeString()
          )}
        </dd>

        <dt>Photos</dt>
        <dd>
          {sync.photosPending === 0 ? (
            'all uploaded'
          ) : (
            // Until it uploads, a photo exists only on this device.
            <strong>{sync.photosPending} waiting to upload</strong>
          )}
        </dd>

        <dt id={ids.storage}>Local storage</dt>
        <dd>
          {durability === null ? (
            <span className="muted">checking…</span>
          ) : durability === 'persisted' ? (
            'persistent'
          ) : (
            <span className="warn">
              not persistent — the browser may evict changes that have not synced yet
            </span>
          )}
        </dd>
      </dl>

      {/* FR-10.7: syncing on demand. Automatic sync is unaffected; this is for
          someone who wants to watch the count fall before closing a laptop. */}
      <div className="actions">
        <button type="button" onClick={onSync}>
          Sync now
        </button>
      </div>
    </div>
  );
}
